import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { v4 as uuid } from 'uuid'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { AdvancedTablePagination } from '@/components/tables/AdvancedTable/AdvancedTablePagination'
import { AiSparkles } from '@/components/ai/AiSparkles'
import { formatTime, truncateString } from '@/utils/helpers'
import WidgetContext from '@/components/widget/widget.context'
import WidgetContainer from '@/components/widget/WidgetContainer'
import WidgetTooltip from '@/components/widget/WidgetTooltip'
import BarChart from '@/components/charts/BarChart'

import { AI_NAME_SUFFIX, formatTotalMinutes, withAiNameSuffix } from './widgetHelpers'

export const CallsByUserWidget = () => {
  const { data, loading } = useSelector((state) => state.analytics)
  const { handleChangeCsvData } = useContext(WidgetContext)
  const [activePage, setActivePage] = useState(1)

  const rows = useMemo(() => {
    return [...data.callCountsByUser]
      .sort((a, b) => a.first_name.localeCompare(b.first_name))
      .map((user) => {
        const name = `${user.first_name} ${user.last_name}`
        const isAiAgent = Boolean(user.ai_agent)

        return {
          ...user,
          name,
          chartName: withAiNameSuffix(name, isAiAgent),
          isAiAgent,
          key: uuid(),
        }
      })
  }, [data])

  const columns = [
    {
      accessor: 'name',
      label: 'Name',
      format: (name, rowData) => (
        <div className="flex">
          {name}
          {rowData.isAiAgent && <AiSparkles />}
        </div>
      ),
    },
    { accessor: 'email', label: 'Email' },
    {
      accessor: 'user_role',
      label: 'User Role',
      format: (v) => (v === 'agent' ? 'Agent' : 'Manager'),
    },
    {
      accessor: 'call_count',
      label: 'Total Call Count',
      format: (v) => v.toLocaleString(),
      headerAlignment: 'right',
    },
    {
      accessor: 'total_duration_seconds',
      label: 'Total Minutes Used',
      format: (value) => formatTotalMinutes(value),
      headerAlignment: 'right',
    },
    {
      accessor: 'last_call',
      label: 'Last Activity',
      format: (v) => formatTime(v),
    },
    {
      accessor: 'last_login',
      label: 'Last Login',
      format: (v) => formatTime(v),
    },
  ]

  // Find pagination values
  const rowsPerPage = 20
  const count = rows.length
  const totalPages = Math.ceil(count / rowsPerPage)
  const paginatedRows = rows
    .slice((activePage - 1) * rowsPerPage, activePage * rowsPerPage)
    .reverse()

  useEffect(() => {
    const headersToExport = columns.map((header) => {
      if (header.accessor === 'name') {
        return { ...header, format: undefined }
      }
      return header
    })
    const csvRows = rows.map((row) => ({
      ...row,
      name: withAiNameSuffix(row.name, row.isAiAgent),
    }))
    handleChangeCsvData({ headers: headersToExport, data: csvRows })
  }, [rows])

  useEffect(() => {
    setActivePage(1)
  }, [data])
  return (
    <WidgetContainer
      large
      loading={loading.usagePage}
      withPagination
      noData={isEmpty(rows)}
      table={
        <AdvancedTable
          index="key"
          defaultOrderBy="name"
          rows={rows}
          columns={columns}
          truncate
          rowsPerPage={10}
          pagination
          enableUpdateRowsPerPage={false}
        />
      }
      chart={
        <>
          <BarChart
            data={paginatedRows}
            keys={['call_count']}
            indexBy="chartName"
            layout="horizontal"
            enableGridY
            margin={{ bottom: 60, left: 150, right: 50, top: 10 }}
            tooltip={(item) => {
              return (
                <WidgetTooltip color={item.color}>
                  <h4>{item.indexValue}</h4>
                  <p>Call Count: {Number(item.value).toLocaleString()}</p>
                </WidgetTooltip>
              )
            }}
            axisBottom={{
              orient: 'bottom',
              tickSize: 5,
              padding: 0.5,
              tickPadding: 5,
              tickRotation: 0,
              legend: 'Call Count',
              legendPosition: 'middle',
              legendOffset: 40,
              format: (v) => Number(v).toLocaleString(),
            }}
            axisLeft={{
              orient: 'left',
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              format: (value) =>
                value.endsWith(AI_NAME_SUFFIX)
                  ? `${truncateString(
                      value.slice(0, -AI_NAME_SUFFIX.length),
                      30 - AI_NAME_SUFFIX.length
                    )}${AI_NAME_SUFFIX}`
                  : truncateString(value, 30),
            }}
          />
          <AdvancedTablePagination
            activePage={activePage}
            rowsPerPage={rowsPerPage}
            count={count}
            totalPages={totalPages}
            setActivePage={setActivePage}
          />
        </>
      }
    />
  )
}
